import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import type { Pt } from '../caza-types.ts';

/**
 * The Long Hunt's chapter 5 grounds from the seed (docs/sendas/barbaro.md, «La guarida»): the rock's wall with its
 * ledges up to the cave's mouth (act 1), the cave's hall with its four mouths (act 2, and the puzzle of their arches),
 * the way down through two chambers to the ash bed (act 3), and the Wolf's ground (act 4): the den with the bed, the
 * mouths, the pillars and the slabs, the crack in its roof, and the summit with its three edges over the sea. The fight's
 * builder (campaign/sendas-caza.ts) and the renderer build them the same way. Integer maths only.
 */

export const CLIMB_RADIUS = 12;
export const HALL_RADIUS = 10;
export const BED_RADIUS = 11;
export const WOLF_RADIUS = 13;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const key = (h: Hex): string => `${h.q},${h.r}`;
const len = (dx: number, dy: number): number => isqrt(dx * dx + dy * dy);

/** The board's rocks (wall, sea, holes): every cell of the map (its radius + 2) that is not floor. */
const rocksBut = (R: number, floor: Set<string>): Hex[] => allCells(R + 2).filter((h) => !floor.has(key(h)));

/** The squared distance from a point to a segment (integer). */
const seg2 = (px: number, py: number, a: Pt, b: Pt): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L2 = dx * dx + dy * dy;
  const tn = (px - a.x) * dx + (py - a.y) * dy;
  let cx = a.x;
  let cy = a.y;
  if (L2 > 0 && tn >= L2) {
    cx = b.x;
    cy = b.y;
  } else if (L2 > 0 && tn > 0) {
    cx = a.x + idiv(dx * tn, L2);
    cy = a.y + idiv(dy * tn, L2);
  }
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
};

/** Points every `step` along a line through `pts` (the first and last included). */
const along = (pts: Pt[], step: number): Pt[] => {
  const out: Pt[] = [{ ...(pts[0] as Pt) }];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as Pt;
    const b = pts[i] as Pt;
    const L = imax(1, len(b.x - a.x, b.y - a.y));
    const n = imax(1, idiv(L + step - 1, step));
    for (let k = 1; k <= n; k++) out.push({ x: a.x + idiv((b.x - a.x) * k, n), y: a.y + idiv((b.y - a.y) * k, n) });
  }
  return out;
};

/* ---------- act 1: the climb ---------- */

export interface ClimbLayout {
  rocks: Hex[];
  /** The ledges' line, bottom to top, and the ledge each point is on; where each ledge begins. */
  way: Pt[];
  ledgeOf: number[];
  ledges: number[];
  /** The corners of the line (the ledges' ends), for the drawing. */
  corners: Pt[];
  nests: Pt[];
  mouth: Pt;
  howlerAt: Pt;
  start: Pt;
  /** The narrow ramp up from each ledge but the last (its middle's cells): the pack on a ledge holds it. */
  ramps: Hex[][];
}

/** The five ledges' heights on the board (south is the sea, north the cave's mouth). */
export const CLIMB_LEDGES = [8600, 5100, 1600, -1900, -5400];
export const CLIMB_MOUTH_Y = -8300;
/** How wide the ledges are, either side of the line. */
export const CLIMB_HALF = 1250;

export const climbLayout = (seed: number): ClimbLayout => {
  const rng = seedRng(deriveSeed(seed, 0xc11b));
  const R = CLIMB_RADIUS;
  const ys = CLIMB_LEDGES;
  // The ledges zig-zag up the wall: east along the lowest, up at its end, west along the next…
  const ends = ys.map(() => 6000 + rngRange(rng, -400, 400));
  const corners: Pt[] = [{ x: -ends[0]!, y: ys[0]! }];
  const ledgeStart: number[] = [0];
  for (let k = 0; k < ys.length; k++) {
    const east = k % 2 === 0;
    const y = ys[k] as number;
    const e = ends[k] as number;
    if (k === ys.length - 1) {
      // The last runs to the middle and up to the cave's mouth.
      corners.push({ x: 0, y });
      corners.push({ x: 0, y: CLIMB_MOUTH_Y + 900 });
      break;
    }
    const x = east ? e : -e;
    corners.push({ x, y });
    // The ramp up to the next ledge, at this end.
    corners.push({ x: x + (east ? 300 : -300), y: ys[k + 1] as number });
    ledgeStart.push(corners.length - 1);
  }
  const way: Pt[] = [];
  const ledgeOf: number[] = [];
  const ledges: number[] = [];
  for (let c = 0; c + 1 < corners.length; c++) {
    const seg = along([corners[c] as Pt, corners[c + 1] as Pt], 1000);
    const k = imax(0, ledgeStart.filter((i) => i <= c).length - 1);
    if (ledgeStart.includes(c)) ledges.push(way.length);
    for (let i = way.length === 0 ? 0 : 1; i < seg.length; i++) {
      way.push(seg[i] as Pt);
      ledgeOf.push(k);
    }
  }
  const mouth = { x: 0, y: CLIMB_MOUTH_Y };
  const floor = new Set<string>();
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    let near = false;
    for (let c = 0; c + 1 < corners.length && !near; c++) if (seg2(p.x, p.y, corners[c] as Pt, corners[c + 1] as Pt) <= CLIMB_HALF * CLIMB_HALF) near = true;
    if (near || len(p.x - mouth.x, p.y - mouth.y) <= 1500) floor.add(key(h));
  }
  // Each ramp's middle (the narrow way up between two ledges).
  const ramps: Hex[][] = ys.slice(0, -1).map(() => []);
  for (const h of allCells(R)) {
    if (!floor.has(key(h))) continue;
    const p = hexToWorld(h.q, h.r);
    for (let k = 0; k < ramps.length; k++) {
      const a = corners[2 * k + 1] as Pt;
      const b = corners[2 * k + 2] as Pt;
      const lo = imax(a.y, b.y) - 1000;
      const hi = imin(a.y, b.y) + 1000;
      if (p.y < lo && p.y > hi && seg2(p.x, p.y, a, b) <= CLIMB_HALF * CLIMB_HALF) (ramps[k] as Hex[]).push(h);
    }
  }
  // The nests: on the wall over each ledge but the lowest.
  const nests: Pt[] = ys.slice(1).map((y) => ({ x: rngRange(rng, -4500, 4500), y: y + 1700 }));
  const hy = ys[3] as number;
  return {
    rocks: rocksBut(R, floor),
    way,
    ledgeOf,
    ledges,
    corners,
    nests,
    mouth,
    howlerAt: { x: rngRange(rng, -1500, 1500), y: hy },
    start: { x: -(ends[0] as number) + 700, y: ys[0] as number },
    ramps,
  };
};

/* ---------- act 2: the mouths of the rock (and the puzzle) ---------- */

/**
 * The arches' puzzle (§4, «Las bocas de la roca»): strikes on arch `i` until it comes down, each fall cracking the
 * standing arches joined to it. `cracks` 3 is down. Returns the cracks after one blow on `i`.
 */
export const archBlow = (cracks: readonly number[], links: readonly [number, number][], i: number, breaks = 3): number[] => {
  const c = cracks.slice();
  if ((c[i] as number) >= breaks) return c;
  c[i] = (c[i] as number) + 1;
  const fall: number[] = (c[i] as number) >= breaks ? [i] : [];
  while (fall.length) {
    const j = fall.shift() as number;
    for (const [a, b] of links) {
      const k = a === j ? b : b === j ? a : -1;
      if (k < 0 || (c[k] as number) >= breaks) continue;
      c[k] = (c[k] as number) + 1;
      if ((c[k] as number) >= breaks) fall.push(k);
    }
  }
  return c;
};

/** Every state the arches can be brought to from `cracks` (as cracks lists, each once). */
const reachable = (cracks: readonly number[], links: readonly [number, number][], breaks = 3): number[][] => {
  const seen = new Set<string>([cracks.join('')]);
  const out: number[][] = [cracks.slice()];
  for (let n = 0; n < out.length; n++) {
    const c = out[n] as number[];
    for (let i = 0; i < c.length; i++) {
      if ((c[i] as number) >= breaks) continue;
      const d = archBlow(c, links, i, breaks);
      const k = d.join('');
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(d);
    }
  }
  return out;
};

/** The most arches that can come down with `keep` standing, from `cracks`, and every set (as bits) that does it. */
export const archBest = (cracks: readonly number[], links: readonly [number, number][], keep: number, breaks = 3): { best: number; sets: number[] } => {
  let best = 0;
  const sets = new Set<number>();
  for (const c of reachable(cracks, links, breaks)) {
    if ((c[keep] as number) >= breaks) continue;
    let bits = 0;
    let n = 0;
    c.forEach((v, i) => {
      if (v >= breaks) {
        bits |= 1 << i;
        n++;
      }
    });
    if (n > best) {
      best = n;
      sets.clear();
    }
    if (n === best) sets.add(bits);
  }
  return { best, sets: [...sets].sort((a, b) => a - b) };
};

/** Struck down first (from `cracks`, blows on it alone), `n` leaves `keep` with fewer to shut than at best. */
export const archTrap = (cracks: readonly number[], links: readonly [number, number][], keep: number, n: number, best: number, breaks = 3): boolean => {
  let c = cracks.slice();
  while ((c[n] as number) < breaks) c = archBlow(c, links, n, breaks);
  if ((c[keep] as number) >= breaks) return true;
  return archBest(c, links, keep, breaks).best < best;
};

/** The ring of cracks in the roof: each arch joined to the next round the hall. */
export const ARCH_LINKS: [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 0],
];

export interface ArchPuzzle {
  cracks: number[];
  trace: number;
  best: number;
}

/**
 * Every puzzle the seed may pick: cracks of 0 to 2 (at least one of two, four to six in all), the mouth without the
 * Wolf's tracks, a single best set with it standing (two or three down), and a neighbour of it that, struck down
 * first, loses that best (§4: «tirar primero a una vecina de esa boca la pierda»).
 */
export const ARCH_PUZZLES: ArchPuzzle[] = (() => {
  const out: ArchPuzzle[] = [];
  for (let m = 0; m < 81; m++) {
    const cracks = [m % 3, idiv(m, 3) % 3, idiv(m, 9) % 3, idiv(m, 27) % 3];
    const sum = cracks.reduce((a, b) => a + b, 0);
    if (sum < 4 || sum > 6 || !cracks.includes(2)) continue;
    for (let trace = 0; trace < 4; trace++) {
      const { best, sets } = archBest(cracks, ARCH_LINKS, trace);
      if (best < 2 || best > 3 || sets.length !== 1) continue;
      const nb = ARCH_LINKS.flatMap(([a, b]) => (a === trace ? [b] : b === trace ? [a] : []));
      if (!nb.some((n) => archTrap(cracks, ARCH_LINKS, trace, n, best))) continue;
      out.push({ cracks, trace, best });
    }
  }
  return out;
})();

export interface HallLayout {
  rocks: Hex[];
  mouths: Pt[];
  arches: Pt[];
  puzzle: ArchPuzzle;
  rim: Pt[];
  start: Pt;
  ivar: Pt;
  hunter: Pt;
}

/** The four mouths' directions round the hall (and the den): north-west, north-east, south-east, south-west. */
const DIAG: Pt[] = [
  { x: -707, y: -707 },
  { x: 707, y: -707 },
  { x: 707, y: 707 },
  { x: -707, y: 707 },
];
const at = (c: Pt, d: Pt, r: number): Pt => ({ x: c.x + idiv(d.x * r, 1000), y: c.y + idiv(d.y * r, 1000) });

export const HALL_R = 6600;

export const hallLayout = (seed: number): HallLayout => {
  const rng = seedRng(deriveSeed(seed, 0xa7c4));
  const R = HALL_RADIUS;
  const c = { x: 0, y: -400 };
  const mouths = DIAG.map((d) => at(c, d, 5400));
  const arches = DIAG.map((d) => at(c, d, 4100));
  const door = { x: 0, y: 7600 };
  const floor = new Set<string>();
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    const inHall = len(p.x - c.x, p.y - c.y) <= HALL_R;
    const inDoor = seg2(p.x, p.y, { x: 0, y: 5600 }, door) <= 1200 * 1200;
    const hole = mouths.some((m) => len(p.x - m.x, p.y - m.y) <= 700);
    if ((inHall || inDoor) && !hole) floor.add(key(h));
  }
  const puzzle = ARCH_PUZZLES[rngInt(rng, ARCH_PUZZLES.length)] as ArchPuzzle;
  return {
    rocks: rocksBut(R, floor),
    mouths,
    arches,
    puzzle: { cracks: puzzle.cracks.slice(), trace: puzzle.trace, best: puzzle.best },
    rim: [
      { x: -1600, y: -6400 },
      { x: 1600, y: -6400 },
      { x: -6200, y: -600 },
      { x: 6200, y: -600 },
    ],
    start: { x: 0, y: 6900 },
    ivar: { x: -6000, y: 300 },
    hunter: { x: -5500, y: 1500 },
  };
};

/* ---------- act 3: the ash bed ---------- */

export interface BedLayout {
  rocks: Hex[];
  rooms: { at: Pt; r: number; holes: Pt[]; gate: Hex[] }[];
  bed: Pt;
  /** The galleries' lines (for the drawing): start to the first chamber, on to each next. */
  ways: Pt[][];
  start: Pt;
}

export const bedLayout = (seed: number): BedLayout => {
  const rng = seedRng(deriveSeed(seed, 0xbed5));
  const R = BED_RADIUS;
  const side = rngInt(rng, 2) === 0 ? 1 : -1;
  // (Far enough apart that each gallery is a corridor of its own, shut whole by its ash.)
  const rooms = [
    { at: { x: 0, y: 6000 }, r: 2400 },
    { at: { x: -3200 * side, y: -200 }, r: 2600 },
    { at: { x: 1800 * side, y: -6300 }, r: 3000 },
  ];
  const start = { x: 0, y: 9000 };
  const ways: Pt[][] = [
    [start, rooms[0]!.at],
    [rooms[0]!.at, rooms[1]!.at],
    [rooms[1]!.at, rooms[2]!.at],
  ];
  const HALF = 1100;
  const floor = new Set<string>();
  const inRoom = (p: Pt, k: number): boolean => len(p.x - rooms[k]!.at.x, p.y - rooms[k]!.at.y) <= rooms[k]!.r;
  const gates: Hex[][] = [[], [], []];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    const room = rooms.some((_, k) => inRoom(p, k));
    let way = -1;
    ways.forEach((w, k) => {
      if (seg2(p.x, p.y, w[0] as Pt, w[1] as Pt) <= HALF * HALF) way = k;
    });
    if (!room && way < 0) continue;
    floor.add(key(h));
    // The way on from a chamber: the gallery's cells between it and the next.
    if (!room && way >= 1) gates[way - 1]!.push(h);
  }
  return {
    rocks: rocksBut(R, floor),
    rooms: rooms.map((r, k) => ({
      ...r,
      holes: [0, 1, 2].map((i) => {
        const a = (k * 2 + i * 2 + 1) % 6;
        const d = [
          { x: 1000, y: 0 },
          { x: 500, y: 866 },
          { x: -500, y: 866 },
          { x: -1000, y: 0 },
          { x: -500, y: -866 },
          { x: 500, y: -866 },
        ][a] as Pt;
        return at(r.at, d, r.r - 700);
      }),
      gate: gates[k] as Hex[],
    })),
    bed: { ...rooms[2]!.at },
    ways,
    start,
  };
};

/* ---------- act 4: the Wolf's ground ---------- */

export interface WolfLayout {
  rocks: Hex[];
  bed: Pt;
  mouths: Pt[];
  pillars: Pt[];
  slabs: { at: Pt; mouth: number; cracks: number }[];
  crackFoot: Pt;
  crackTop: Pt;
  summit: Pt;
  edges: Pt[];
  island: number;
  stones: Pt[];
  skiff: Pt;
  denRim: Pt[];
  topRim: Pt[];
  ulla: Pt;
  ivarHurt: Pt;
  ivarUp: Pt;
  sten: Pt;
  start: Pt;
}

/** The den's middle (the ash bed) and its floor's reach; the summit's middle and its reach. */
export const DEN_AT: Pt = { x: 0, y: 5200 };
export const DEN_R = 5000;
export const TOP_AT: Pt = { x: 0, y: -6600 };
export const TOP_R = 4300;
/** The crack up from the den to the summit, either side of its line. */
export const CRACK_HALF = 950;

export const wolfLayout = (seed: number): WolfLayout => {
  const rng = seedRng(deriveSeed(seed, 0x9a01));
  const R = WOLF_RADIUS;
  const bed = DEN_AT;
  const mouths = DIAG.map((d) => at(bed, d, 4300));
  const pillars = [
    { x: 0, y: bed.y - 2300 },
    { x: 2300, y: bed.y },
    { x: 0, y: bed.y + 2300 },
    { x: -2300, y: bed.y },
  ];
  const slabs = DIAG.map((d, k) => ({ at: at(bed, d, 2500), mouth: k, cracks: rngInt(rng, 2) }));
  const crackFoot = { x: 0, y: bed.y - DEN_R + 400 };
  const crackTop = { x: 0, y: TOP_AT.y + TOP_R - 400 };
  const summit = TOP_AT;
  const edges = [
    { x: summit.x - 3800, y: summit.y },
    { x: summit.x, y: summit.y - 3800 },
    { x: summit.x + 3800, y: summit.y },
  ];
  const floor = new Set<string>();
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    const den = len(p.x - bed.x, p.y - bed.y) <= DEN_R;
    const top = len(p.x - summit.x, p.y - summit.y) <= TOP_R;
    const crack = seg2(p.x, p.y, crackFoot, crackTop) <= CRACK_HALF * CRACK_HALF;
    const hole = mouths.some((m) => len(p.x - m.x, p.y - m.y) <= 650);
    const pillar = pillars.some((m) => len(p.x - m.x, p.y - m.y) <= 450);
    if ((den || top || crack) && !hole && !pillar) floor.add(key(h));
  }
  return {
    rocks: rocksBut(R, floor),
    bed: { ...bed },
    mouths,
    pillars,
    slabs,
    crackFoot,
    crackTop,
    summit: { ...summit },
    edges,
    island: 2,
    stones: [
      { x: summit.x - 3000, y: summit.y - 1700 },
      { x: summit.x - 1300, y: summit.y - 3300 },
      { x: summit.x + 1300, y: summit.y - 3300 },
      { x: summit.x + 3000, y: summit.y - 1700 },
    ],
    skiff: { x: summit.x - 2900, y: summit.y + 2900 },
    denRim: [
      { x: bed.x - 4200, y: bed.y + 2200 },
      { x: bed.x + 4200, y: bed.y + 2200 },
      { x: bed.x, y: bed.y + 4500 },
    ],
    topRim: [
      { x: summit.x - 2600, y: summit.y - 2900 },
      { x: summit.x + 2600, y: summit.y - 2900 },
      { x: summit.x + 2900, y: summit.y + 2700 },
    ],
    ulla: { x: 1700, y: bed.y - DEN_R + 1300 },
    ivarHurt: { x: bed.x - 4200, y: bed.y - 1400 },
    ivarUp: { x: crackTop.x + 900, y: crackTop.y - 600 },
    sten: { x: summit.x - 3300, y: summit.y + 3300 },
    start: { x: 0, y: bed.y + 3300 },
  };
};
